import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { projectAPI } from "../services/api";

const ProjectDetailPage = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    const fetchProject = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log("🔍 Fetching project with ID:", id);
        const response = await projectAPI.getById(id);
        console.log("📦 Full API Response:", response);
        console.log("📊 Response data:", response.data);

        if (response.data && response.data.success) {
          console.log("✅ Project data received:", response.data.data);
          setProject(response.data.data);
        } else {
          console.error("❌ API returned error:", response.data?.message);
          setError(response.data?.message || "ไม่พบข้อมูลโครงการ");
        }
      } catch (error) {
        console.error("❌ Failed to fetch project:", error);
        if (error.response) {
          console.error("Error response:", error.response.data);
        }
        setError(error.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล");
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  const formatBudget = (budget) => {
    return new Intl.NumberFormat("th-TH", {
      style: "currency",
      currency: "THB",
      minimumFractionDigits: 2,
    }).format(budget || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "ไม่ระบุ";
    const date = new Date(dateString);
    return date.toLocaleDateString("th-TH", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // ฟังก์ชันสำหรับสร้าง URL รูปภาพ
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;

    // ถ้าเป็น URL เต็มแล้ว
    if (imagePath.startsWith("http")) {
      return imagePath;
    }

    // ถ้าเป็น relative path ที่ขึ้นต้นด้วย ../
    if (imagePath.startsWith("../")) {
      // ตัด '../' ออก แล้วต่อด้วย base URL
      const cleanPath = imagePath.replace(/\.\.\//g, "");
      return `https://asc.sskru.ac.th/backend/${cleanPath}`;
    }

    // ถ้าเป็น path ปกติ
    return `https://asc.sskru.ac.th/backend/${imagePath}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50 py-12">
        <div className="text-center max-w-md mx-auto p-8 bg-white rounded-xl shadow-lg">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-exclamation-circle text-4xl text-red-500"></i>
          </div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            {error || "ไม่พบข้อมูลโครงการ"}
          </h2>
          <p className="text-gray-600 mb-6">ไม่พบโครงการที่คุณต้องการ</p>
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors"
          >
            <i className="fas fa-arrow-left"></i>
            กลับไปหน้ารายการโครงการ
          </Link>
        </div>
      </div>
    );
  }

  console.log("Rendering project:", project);

  return (
    <>
      <Helmet>
        <title>
          {project?.project_name
            ? `${project.project_name} | พื้นที่บริการวิชาการ`
            : "พื้นที่บริการวิชาการ | มหาวิทยาลัยราชภัฏศรีสะเกษ"}
        </title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container-custom">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-8 flex-wrap">
            <Link
              to="/"
              className="text-primary-500 hover:text-primary-600 transition-colors"
            >
              หน้าแรก
            </Link>
            <i className="fas fa-chevron-right text-xs"></i>
            <Link
              to="/projects"
              className="text-primary-500 hover:text-primary-600 transition-colors"
            >
              พื้นที่บริการวิชาการ
            </Link>
            <i className="fas fa-chevron-right text-xs"></i>
            <span className="text-gray-800 font-medium line-clamp-1">
              {project.project_name}
            </span>
          </div>

          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-primary-500">
              {project.project_name}
            </h1>
            <span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
              style={{
                backgroundColor: project.fiscal_year ? "#28a745" : "#ffc107",
                color: project.fiscal_year ? "#fff" : "#000",
              }}
            >
              <i className="fas fa-calendar"></i>
              {project.fiscal_year ? `ปีงบ ${project.fiscal_year}` : "ไม่ระบุ"}
            </span>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content - 2/3 width */}
            <div className="lg:col-span-2 space-y-6">
              {/* Project Image - ถ้ามี */}
              {project.main_image_url && (
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                  <img
                    src={getImageUrl(project.main_image_url)}
                    alt={project.project_name}
                    className="w-full h-auto object-cover"
                    onError={(e) => {
                      console.error(
                        "Image failed to load:",
                        project.main_image_url,
                      );
                      e.target.onerror = null;
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}

              {/* General Info */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-semibold text-primary-500 mb-4 flex items-center">
                  <i className="fas fa-info-circle text-secondary-500 mr-2"></i>
                  ข้อมูลทั่วไป
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">หน่วยงาน</p>
                    <p
                      className="font-medium"
                      style={{ color: project.dpt_color || "#195599" }}
                    >
                      {project.dpt_name || "ไม่ระบุ"}
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    {project.mem_img && (
                      <img
                        src={getImageUrl(project.mem_img)}
                        alt={`${project.mem_fname} ${project.mem_sname}`}
                        className="w-12 h-12 rounded-full object-cover border-2 border-primary-200"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = "none";
                        }}
                      />
                    )}
                    <div>
                      <p className="text-sm text-gray-500">หัวหน้าโครงการ</p>
                      <p className="font-medium">
                        {project.mem_fname || ""} {project.mem_sname || ""}
                      </p>
                      {project.mem_email && (
                        <p className="text-xs text-gray-500">
                          {project.mem_email}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Background */}
              {project.background && (
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-semibold text-primary-500 mb-4 flex items-center">
                    <i className="fas fa-history text-secondary-500 mr-2"></i>
                    หลักการและเหตุผล
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {project.background}
                  </p>
                </div>
              )}

              {/* Needs */}
              {project.needs && (
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-semibold text-primary-500 mb-4 flex items-center">
                    <i className="fas fa-hand-holding-heart text-secondary-500 mr-2"></i>
                    ความต้องการของชุมชน
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {project.needs}
                  </p>
                </div>
              )}

              {/* Objectives */}
              {project.objectives && project.objectives.length > 0 && (
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-semibold text-primary-500 mb-4 flex items-center">
                    <i className="fas fa-bullseye text-secondary-500 mr-2"></i>
                    วัตถุประสงค์
                  </h3>
                  <ul className="space-y-2">
                    {project.objectives.map((obj, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-green-500 mt-1">✓</span>
                        <span className="text-gray-700">{obj}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Target Groups */}
              {project.target_groups && project.target_groups.length > 0 && (
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-semibold text-primary-500 mb-4 flex items-center">
                    <i className="fas fa-users text-secondary-500 mr-2"></i>
                    กลุ่มเป้าหมาย
                  </h3>
                  <ul className="space-y-2">
                    {project.target_groups.map((group, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-blue-500 mt-1">•</span>
                        <span className="text-gray-700">
                          {group.target_name} จำนวน {group.target_amount} คน
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Duration */}
              {(project.start_date || project.end_date) && (
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-semibold text-primary-500 mb-4 flex items-center">
                    <i className="fas fa-clock text-secondary-500 mr-2"></i>
                    ระยะเวลาดำเนินการ
                  </h3>
                  <p className="text-gray-700">
                    {formatDate(project.start_date)} -{" "}
                    {formatDate(project.end_date)}
                  </p>
                </div>
              )}

              {/* Outputs */}
              {project.outputs && project.outputs.length > 0 && (
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-semibold text-primary-500 mb-4 flex items-center">
                    <i className="fas fa-box text-secondary-500 mr-2"></i>
                    ผลผลิต (Output)
                  </h3>
                  <ul className="space-y-2">
                    {project.outputs.map((output, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-purple-500 mt-1">📦</span>
                        <span className="text-gray-700">
                          {output.detail} จำนวน {output.quantity} {output.unit}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Results */}
              {project.results && project.results.length > 0 && (
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-semibold text-primary-500 mb-4 flex items-center">
                    <i className="fas fa-chart-line text-secondary-500 mr-2"></i>
                    ผลลัพธ์ (Outcome)
                  </h3>
                  <ul className="space-y-2">
                    {project.results.map((result, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-green-500 mt-1">📈</span>
                        <span className="text-gray-700">
                          {result.detail} {result.quantity} {result.unit}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Impacts */}
              {project.impacts && project.impacts.length > 0 && (
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-semibold text-primary-500 mb-4 flex items-center">
                    <i className="fas fa-star text-secondary-500 mr-2"></i>
                    ผลกระทบ (Impact)
                  </h3>
                  <ul className="space-y-2">
                    {project.impacts.map((impact, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-yellow-500 mt-1">⭐</span>
                        <span className="text-gray-700">
                          <strong>{impact.title}:</strong> {impact.detail}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Sidebar - 1/3 width */}
            {/* Sidebar - 1/3 width */}
            <aside className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="flex flex-col space-y-6 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                  {/* Location */}
                  <div className="bg-white rounded-xl shadow-lg p-6 flex-shrink-0">
                    <h3 className="text-lg font-semibold text-primary-500 mb-4 flex items-center">
                      <i className="fas fa-map-marker-alt text-secondary-500 mr-2"></i>
                      สถานที่ดำเนินการ
                    </h3>
                    <p className="text-gray-700 mb-4 flex items-start gap-2">
                      <i className="fas fa-map-marker-alt text-primary-500 mt-1"></i>
                      <span>
                        {project.subdistrict || ""} {project.district || ""}{" "}
                        {project.province || ""}
                      </span>
                    </p>
                    {project.lat && project.lng && (
                      <a
                        href={`https://www.google.com/maps?q=${project.lat},${project.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 w-full justify-center bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
                      >
                        <i className="fas fa-map"></i>
                        ดูใน Google Maps
                      </a>
                    )}
                  </div>

                  {/* Team */}
                  {project.team && project.team.length > 0 && (
                    <div className="bg-white rounded-xl shadow-lg p-6 flex-shrink-0">
                      <h3 className="text-lg font-semibold text-primary-500 mb-4 flex items-center">
                        <i className="fas fa-users text-secondary-500 mr-2"></i>
                        คณะทำงาน
                      </h3>
                      <ul className="space-y-3">
                        {project.team.map((member, index) => (
                          <li
                            key={index}
                            className="border-b last:border-0 pb-2 last:pb-0"
                          >
                            <p className="font-medium text-gray-800">
                              {member.mem_fname || ""} {member.mem_sname || ""}
                            </p>
                            {member.role && (
                              <p className="text-sm text-gray-500">
                                {member.role}
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Project Images */}
                  {project.images && project.images.length > 0 && (
                    <div className="bg-white rounded-xl shadow-lg p-6 flex-shrink-0">
                      <h3 className="text-xl font-semibold text-primary-500 mb-4 flex items-center">
                        <i className="fas fa-images text-secondary-500 mr-2"></i>
                        ภาพกิจกรรม
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        {project.images.map((img, index) => {
                          const imageUrl = img.image_url.startsWith("http")
                            ? img.image_url
                            : img.image_url.startsWith("/")
                              ? img.image_url
                              : `/${img.image_url}`;

                          return (
                            <div
                              key={index}
                              className="relative group cursor-pointer rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all aspect-square"
                              onClick={() => setLightboxIndex(index)}
                            >
                              <img
                                src={imageUrl}
                                alt={`ภาพกิจกรรม ${index + 1}`}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "/assets/images/no-image.png";
                                }}
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                                <i className="fas fa-search-plus text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity"></i>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Back Button */}
                  <div className="bg-white rounded-xl shadow-lg p-6 flex-shrink-0">
                    <Link
                      to="/projects"
                      className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 transition-colors"
                    >
                      <i className="fas fa-arrow-left"></i>
                      กลับไปหน้ารายการโครงการ
                    </Link>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && project.images && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
          onClick={() => setLightboxIndex(null)}
        >
          <button
            onClick={() => setLightboxIndex(null)}
            className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 transition-colors z-10"
          >
            ×
          </button>

          {project.images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((prev) =>
                    prev === 0 ? project.images.length - 1 : prev - 1,
                  );
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-5xl hover:text-gray-300 transition-colors"
              >
                ‹
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((prev) =>
                    prev === project.images.length - 1 ? 0 : prev + 1,
                  );
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-5xl hover:text-gray-300 transition-colors"
              >
                ›
              </button>
            </>
          )}

          <img
            src={
              project.images[lightboxIndex].image_url.startsWith("http")
                ? project.images[lightboxIndex].image_url
                : project.images[lightboxIndex].image_url.startsWith("/")
                  ? project.images[lightboxIndex].image_url
                  : `/${project.images[lightboxIndex].image_url}`
            }
            alt=""
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/assets/images/no-image.png";
            }}
          />

          {project.images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-2 rounded-full">
              {lightboxIndex + 1} / {project.images.length}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ProjectDetailPage;
